import React, {useState, useEffect, useContext} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./AppDomainDataStreamCreation.css";
import API, {Resolution} from "../../logic/API";
import {DataStream, TopicDescriptionInfo} from "../../logic/APITypes";
import openInNewTabIcon from "../../images/openInNewTab.svg";
import {
  AuxiliaryToolTip,
  Button,
  Icon,
  IconButton,
  LoadingSkeleton,
  TextInput,
} from "@spg/component_library";
import {SnackbarContext} from "../../reusables/SnackbarProvider";
import {Link} from "react-router-dom";
import closeCircle from "../../images/closeCircle.svg";

function TopicEntry(props: {
  topic: TopicDescriptionInfo;
  onClick: () => void;
  isDisabled: boolean;
}) {
  const intl = useIntl();
  return (
    <div className="appDomainDataStreamCreationTopicEntry">
      <AuxiliaryToolTip
        toolTip={props.topic.name}
        idBase={"appDomainDataStreamCreationTopicEntry" + props.topic.id}
        tooltipPosition="bottom"
        overflowActivated
      >
        <button
          id={"appDomainDataStreamCreationTopicEntry" + props.topic.id}
          className="appDomainDataStreamCreationTopicEntryButton"
          onClick={props.onClick}
          disabled={props.isDisabled}
        >
          {props.topic.name}
        </button>
      </AuxiliaryToolTip>
      <Link
        to={"/topics/overview/" + props.topic.id}
        target="_blank"
        rel="noreferrer"
      >
        <Icon
          title={intl.formatMessage({
            id: "appDomainCreation.modal.streams.link",
          })}
          href={openInNewTabIcon + "#globalG"}
          iconSize={{width: 20, height: 20}}
          strokeColor="var(--colorBase900)"
        />
      </Link>
    </div>
  );
}

type AppDomainDataStreamCreationProps = {
  /** The ID of the currently opened app domain */
  domainId: string;
  /** Function to call when the action is aborted */
  onCancel: () => void;
  /** Function to call when the stream was successfully created or edited */
  onConfirm: (stream?: DataStream) => void;
  /** Stream to modify or undefined to create a new stream */
  stream?: DataStream;
};

/**
 * The contents of the modal when creating a data stream.
 * Includes a textbox for naming, a search bar, a list of topics to select from and buttons to confirm or cancel.
 */
function AppDomainDataStreamCreation(props: AppDomainDataStreamCreationProps) {
  const intl = useIntl();
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const [topics, setTopics] = useState<Resolution<TopicDescriptionInfo[]>>({
    success: false,
    unresolved: true,
  });
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [streamName, setStreamName] = useState(props.stream?.name || "");
  const [selectedTopics, setSelectedTopics] = useState<TopicDescriptionInfo[]>(
    [],
  );
  const [search, setSearch] = useState("");

  const simplifiedSearch = search.trim().toLowerCase();
  const searchResults = (topics.payload || []).filter(
    (t) =>
      !selectedTopics.includes(t) &&
      (t.name.toLowerCase().includes(simplifiedSearch) ||
        t.id === simplifiedSearch),
  );

  useEffect(() => {
    API.getTopicDescriptions().then((response) => {
      const topics = response.payload;
      if (topics) topics.sort((a, b) => a.name.localeCompare(b.name));
      setTopics({
        ...response,
        payload: topics,
      });
    });
  }, []);

  return (
    <div className="appDomainDataStreamCreation">
      <TextInput
        label={intl.formatMessage({
          id: "appDomainCreation.modal.streams.name",
        })}
        idBase={"appDomainDataStreamCreationStreamName"}
        value={streamName}
        setValue={setStreamName}
        isReadOnly={!!props.stream}
        isDisabled={waitingForResponse}
      />
      <TextInput
        label={intl.formatMessage({
          id: "appDomainCreation.modal.streams.search",
        })}
        idBase={"appDomainDataStreamCreationSearch"}
        placeholder={intl.formatMessage({
          id: "appDomainCreation.modal.streams.search.placeholder",
        })}
        value={search}
        setValue={setSearch}
        isDisabled={waitingForResponse}
      />
      <div className="appDomainDataStreamCreationTopicContainer">
        {selectedTopics.map((topic) => (
          <span key={topic.id} className="appDomainDataStreamCreationTopicChip">
            <span>{topic.name}</span>
            <IconButton
              idBase={"appDomainDataStreamCreationRemove" + topic.id}
              onClick={() =>
                setSelectedTopics((oldTopics) =>
                  oldTopics.filter((t) => t.id !== topic.id),
                )
              }
              buttonSize={{height: 24, width: 32}}
              iconProps={{
                href: closeCircle + "#globalG",
                strokeColor: "#101223",
                title: intl.formatMessage({
                  id: "appDomainCreation.modal.streams.remove",
                }),
              }}
            />
          </span>
        ))}
      </div>
      {!topics.unresolved &&
        (!topics.success || searchResults.length === 0) && (
          <div className="appDomainDataStreamCreationBlank">
            <FormattedMessage
              id={
                "appDomainCreation.modal.streams.topics." +
                (!topics.success
                  ? "error"
                  : topics.payload?.length === 0
                    ? "noTopics"
                    : selectedTopics.length === 0
                      ? "emptySearch"
                      : "all")
              }
            />
          </div>
        )}
      <ul className="defaultScrollbar">
        {topics.unresolved
          ? [1, 2, 3].map((i) => (
              <li key={i}>
                <div className="appDomainDataStreamCreationTopicEntry">
                  <LoadingSkeleton />
                </div>
              </li>
            ))
          : searchResults.map((t) => (
              <li key={t.id}>
                <TopicEntry
                  topic={t}
                  onClick={() => {
                    setSelectedTopics((oldTopics) => [...oldTopics, t]);
                  }}
                  isDisabled={waitingForResponse}
                />
              </li>
            ))}
      </ul>
      <div className="appDomainDataStreamCreationModalButtons">
        <Button
          label={intl.formatMessage({
            id: "appDomainCreation.modal.streams.cancel",
          })}
          idBase={"appDomainDataStreamCreationModalCancel"}
          onClick={props.onCancel}
          configuration={"tertiary"}
          isDisabled={waitingForResponse}
        />
        <Button
          label={intl.formatMessage({
            id: `appDomainCreation.modal.streams.${
              props.stream ? "edit" : "create"
            }.confirm`,
          })}
          isDisabled={
            waitingForResponse ||
            streamName.trim() === "" ||
            selectedTopics.length === 0
          }
          idBase={"appDomainDataStreamCreationModalConfirm"}
          onClick={() => {
            setWaitingForResponse(true);
            if (!props.stream)
              API.createDataStream(
                props.domainId,
                streamName,
                selectedTopics.map((topic) => topic.id),
              ).then((response) => {
                setWaitingForResponse(false);
                if (response.success && response.payload) {
                  props.onConfirm(response.payload);
                  addSnackbar({
                    variant: "timeout",
                    text: intl.formatMessage({
                      id: "appDomainCreation.modal.streams.create.success",
                    }),
                    timeout: 7000,
                  });
                } else
                  addSnackbar({
                    variant: "close",
                    text: intl.formatMessage({
                      id: "appDomainCreation.modal.streams.create.error",
                    }),
                    hasCloseButton: true,
                  });
              });
            else
              API.addTopicsToDataStream(
                props.domainId,
                props.stream.id,
                selectedTopics.map((topic) => topic.id),
              ).then((response) => {
                setWaitingForResponse(false);
                if (response.success) {
                  props.onConfirm();
                  addSnackbar({
                    variant: "timeout",
                    text: intl.formatMessage({
                      id: "appDomainCreation.modal.streams.edit.success",
                    }),
                    timeout: 7000,
                  });
                } else {
                  props.onCancel();
                  addSnackbar({
                    variant: "close",
                    text: intl.formatMessage({
                      id: "appDomainCreation.modal.streams.edit.error",
                    }),
                    hasCloseButton: true,
                  });
                }
              });
          }}
          configuration={"primary"}
        />
      </div>
    </div>
  );
}

export {AppDomainDataStreamCreation};
